import supabase from "./supabaseClient";
import { getRoomFileUrl } from "../lib/roomFileStorage";

export const BOARD_VERSION = 1;

export function emptyBoard() {
  return { version: BOARD_VERSION, viewport: { x: 0, y: 0, scale: 1 }, gridMode: "dots", elements: [] };
}

function serializeElements(elements) {
  return (elements || []).map((el) => {
    if (el.type === "image_slot") {
      const { options, details, captions, busy, error, ...rest } = el;
      return { ...rest, options: [], details: [], captions: [], index: 0 };
    }
    if (el.type !== "image") return el;
    const { src, ...rest } = el;
    return el.storagePath ? rest : { ...rest, src };
  });
}

async function resolveElements(elements) {
  return Promise.all(
    (elements || []).map(async (el) => {
      if (el.type !== "image" || !el.storagePath) return el;
      try {
        const src = await getRoomFileUrl(el.storagePath);
        return { ...el, src };
      } catch (err) {
        console.warn("[whiteboard] could not resolve image", el.storagePath, err);
        return el;
      }
    })
  );
}

export async function loadWhiteboard(userId, roomId) {
  if (!userId || !roomId) return null;

  try {
    const { data, error } = await supabase
      .from("whiteboards")
      .select("id, board_json")
      .eq("user_id", userId)
      .eq("room_id", roomId)
      .maybeSingle();

    if (error) {
      console.warn("[whiteboard] load failed:", error.message);
      return null;
    }
    if (!data?.board_json) return null;

    const board = { ...emptyBoard(), ...data.board_json };
    board.elements = await resolveElements(board.elements);
    return { id: data.id, board };
  } catch (err) {
    console.warn("[whiteboard] load threw:", err?.message || err);
    return null;
  }
}

export async function loadWhiteboardElements(userId, roomId) {
  if (!userId || !roomId) return null;
  try {
    const { data, error } = await supabase
      .from("whiteboards")
      .select("board_json")
      .eq("user_id", userId)
      .eq("room_id", roomId)
      .maybeSingle();
    if (error) return null;
    return Array.isArray(data?.board_json?.elements) ? data.board_json.elements : [];
  } catch {
    return null;
  }
}

export async function saveWhiteboard(userId, roomId, board) {
  if (!userId || !roomId) return { ok: false, skipped: true };

  const payload = {
    user_id: userId,
    room_id: roomId,
    board_json: {
      version: BOARD_VERSION,
      viewport: board.viewport || { x: 0, y: 0, scale: 1 },
      gridMode: board.gridMode || "dots",
      elements: serializeElements(board.elements),
    },
    updated_at: new Date().toISOString(),
  };

  try {
    const { data, error } = await supabase
      .from("whiteboards")
      .upsert(payload, { onConflict: "user_id,room_id" })
      .select("id")
      .maybeSingle();

    if (error) {
      console.warn("[whiteboard] save failed:", error.message);
      return { ok: false, error: error.message };
    }
    return { ok: true, id: data?.id || null };
  } catch (err) {
    console.warn("[whiteboard] save threw:", err?.message || err);
    return { ok: false, error: err?.message || "save failed" };
  }
}

export async function saveVersion(userId, whiteboardId, board, label) {
  if (!userId || !whiteboardId) return { ok: false, skipped: true };

  try {
    const { data, error } = await supabase
      .from("whiteboard_versions")
      .insert({
        whiteboard_id: whiteboardId,
        user_id: userId,
        label: label || null,
        version_json: {
          version: BOARD_VERSION,
          viewport: board.viewport || { x: 0, y: 0, scale: 1 },
          gridMode: board.gridMode || "dots",
          elements: serializeElements(board.elements),
        },
      })
      .select("id, label, created_at")
      .maybeSingle();

    if (error) {
      console.warn("[whiteboard] version save failed:", error.message);
      return { ok: false, error: error.message };
    }
    return { ok: true, version: data };
  } catch (err) {
    console.warn("[whiteboard] version save threw:", err?.message || err);
    return { ok: false, error: err?.message || "save failed" };
  }
}

export async function listVersions(whiteboardId) {
  if (!whiteboardId) return [];
  try {
    const { data, error } = await supabase
      .from("whiteboard_versions")
      .select("id, label, created_at")
      .eq("whiteboard_id", whiteboardId)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      console.warn("[whiteboard] version list failed:", error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn("[whiteboard] version list threw:", err?.message || err);
    return [];
  }
}

export async function loadVersion(versionId) {
  if (!versionId) return null;
  try {
    const { data, error } = await supabase
      .from("whiteboard_versions")
      .select("id, label, created_at, version_json")
      .eq("id", versionId)
      .maybeSingle();

    if (error || !data?.version_json) {
      if (error) console.warn("[whiteboard] version load failed:", error.message);
      return null;
    }
    const board = { ...emptyBoard(), ...data.version_json };
    board.elements = await resolveElements(board.elements);
    return { id: data.id, label: data.label, created_at: data.created_at, board };
  } catch (err) {
    console.warn("[whiteboard] version load threw:", err?.message || err);
    return null;
  }
}

const whiteboardService = {
  emptyBoard, loadWhiteboard, saveWhiteboard,
  saveVersion, listVersions, loadVersion,
  BOARD_VERSION,
};
export default whiteboardService;
